import React from 'react';

import { Box, styled as MUIStyled, Rating, TextareaAutosize, Typography } from '@mui/material';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { Reservation, User } from '../../../graphql/API';
import { useVenueRateUserMutation } from '../../../hooks/reactQuery/reservations/useVenueRateUserMutation';
import useMutationSaveUser from '../../../hooks/reactQuery/useMutationSaveUser';
import Icons from '../../../themes/icons';
import { getRatingColor } from '../../../utils/rating';
import Button from '../../Button/Button';
import SaveUserButton from '../../Button/SaveUserButton';
import BaseDialog, { BaseDialogProps } from '../Base';

type Props = { reservation?: Reservation; userData?: User } & BaseDialogProps;

const StyledRating = MUIStyled(Rating, {
  shouldForwardProp: (prop) => prop !== 'ratingValue',
})<{ ratingValue: number }>(({ ratingValue }) => ({
  '& .MuiRating-iconFilled': {
    color: getRatingColor(ratingValue),
  },
  '& .MuiRating-iconEmpty': {
    color: '#eee', // Non-filled (empty) star color
  },
  fontSize: '64px',
  gap: '26px',
}));

export const ReservationRatingDialog: React.FC<Props> = ({
  reservation,
  userData,
  ...restProps
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [userRating, setUserRating] = React.useState(0);
  const [hoverRating, setHoverRating] = React.useState(-1);
  const [feedback, setFeedback] = React.useState('');
  const [autoSaved, setAutoSaved] = React.useState(false);

  const { saveUserMutation } = useMutationSaveUser({});

  React.useEffect(() => {
    if (
      userRating === 5 &&
      !autoSaved &&
      !userData?.savedByVenue &&
      reservation?.userID
    ) {
      saveUserMutation.mutate(Number(reservation.userID));
      setAutoSaved(true);
    }
  }, [userRating, autoSaved, userData?.savedByVenue, reservation?.userID, saveUserMutation]);

  const postVenueRateUser = useVenueRateUserMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservation-detail'] });
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      enqueueSnackbar(`Thank you for leaving a review for ${reservation?.user?.firstname}!`, {
        variant: 'success',
      });
      restProps?.onClose?.({}, 'backdropClick');
    },
  });

  const onRatingChange = (rating: number) => {
    setUserRating(rating);
  };

  const handleFeedback = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFeedback((event.target as HTMLTextAreaElement).value);
  };

  const handleSubmit = () => {
    if (reservation?.id) {
      postVenueRateUser.mutate({
        reservationID: Number(reservation?.id),
        userID: reservation.userID,
        rating: userRating,
      });
    }
  };

  return (
    <BaseDialog width='500px' height={userRating > 0 ? '700px' : '650px'} {...restProps}>
      <Box px={2} py={3}>
        <Typography fontSize={'24px'} fontWeight={500}>
          Rate {reservation?.user?.firstname} ’s post for your offer
        </Typography>
        <Box mt={'42px'}>
          <StyledRating
            name='collab-rating-controlled'
            value={userRating}
            ratingValue={hoverRating !== -1 ? hoverRating : userRating}
            onChange={(_, newValue) => {
              if (newValue) onRatingChange(newValue);
            }}
            onChangeActive={(_, newHover) => {
              setHoverRating(newHover);
            }}
            size='large'
          />
          <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} mt={0.5}>
            <Typography color={'#D04040'} fontSize={'15px'} fontFamily={'Poppins'} fontWeight={500}>
              Very poor
            </Typography>
            <Typography color={'#37BB90'} fontSize={'15px'} fontFamily={'Poppins'} fontWeight={500}>
              Excellent
            </Typography>
          </Box>
        </Box>

        <Box mt={6}>
          <Typography fontSize={'15px'} fontWeight={500} mb={1}>
            Feedback
          </Typography>
          <TextareaAutosize
            value={feedback}
            onChange={handleFeedback}
            minRows={6}
            placeholder='Share feedback on the content or collaboration experience (Optional)'
            style={{
              borderRadius: '8px',
              border: '1px solid #F1F1F1',
              resize: 'none',
              padding: '20px',
              width: '100%',
            }}
          />
        </Box>
        {userRating > 0 && userRating <= 1 && (
          <Box display={'flex'} alignItems={'center'} gap={'8px'} my={2}>
            <Icons.WarningIcon />
            <Typography fontSize={'15px'} fontFamily={'Poppins'}>
              Once you submit this rating, this{' '}
              <Typography fontSize={'15px'} component={'span'} fontWeight={600}>
                member cannot book
              </Typography>{' '}
              your offers again.
            </Typography>
          </Box>
        )}
        {userRating === 5 && (
          <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} my={3}>
            <Typography>Save this member for future collaboration</Typography>
            <SaveUserButton
              saved={Boolean(userData?.savedByVenue) || autoSaved}
              userId={Number(reservation?.userID)}
              sx={{ p: 0, m: 0, minWidth: 0 }}
              isOnlineBrand
            />
          </Box>
        )}
        <Button
          fullWidth
          variant='contained'
          color='primary'
          sx={{ py: 1.6, mt: 3 }}
          disabled={userRating <= 0 || postVenueRateUser.isPending}
          onClick={handleSubmit}
        >
          {postVenueRateUser.isPending ? 'Submitting...' : 'Submit'}
        </Button>
      </Box>
    </BaseDialog>
  );
};
